import React, { createContext, useContext } from 'react';
import { Download } from 'lucide-react';
import { CsvColumn, csvFilename, downloadCsv, toCsv } from '../utils/chartCsv';
import Button from './ui/Button';

/**
 * What the downloaded file is about, set once by the page that lays the charts
 * out: the device (or the compared set) and the requested window. The charts
 * themselves don't know which device they are drawing.
 */
export interface ChartCsvScope {
  subject?: string;
  startDate?: string;
  endDate?: string;
}

export const ChartCsvContext = createContext<ChartCsvScope>({});

interface ChartCsvButtonProps<Row> {
  /** Short chart name for the filename, e.g. 'pressure'. */
  chart: string;
  rows: Row[];
  columns: CsvColumn<Row>[];
  /** The window this chart actually covers, when it differs from the page's. */
  startDate?: string;
  endDate?: string;
}

/** Download arrow that saves the chart's plotted rows as a CSV file. */
const ChartCsvButton = <Row,>({ chart, rows, columns, ...range }: ChartCsvButtonProps<Row>) => {
  const scope = useContext(ChartCsvContext);
  const { subject } = scope;
  const startDate = range.startDate ?? scope.startDate;
  const endDate = range.endDate ?? scope.endDate;
  const filename = csvFilename([subject, chart, startDate, endDate && `to-${endDate}`]);
  return (
    <Button
      variant="icon"
      onClick={() => downloadCsv(filename, toCsv(rows, columns))}
      disabled={rows.length === 0}
      title={`Download this chart's data as CSV (${filename})`}
      aria-label="Download chart data as CSV"
      className="shrink-0"
    >
      <Download size={18} aria-hidden />
    </Button>
  );
};

export default ChartCsvButton;
